import latinize from 'latinize'
import {usePathname} from 'next/navigation'
import {useEffect, useState} from 'react'

import {AcademyContents} from '@/utils/contentful'
import {routeTo} from '@/utils/router'
import {analytics} from '@/utils/tracking/analytics'

const analyticsDebounceIntervalMs = 300

const normalize = (text: string) => latinize(text).toLowerCase()

// An empty query returns every article, which the desktop search shows as suggestions.
export const useAcademySearch = (contents: AcademyContents) => {
  const [query, setQuery] = useState('')
  const trimmedQuery = query.trim()
  const pathname = usePathname()

  // The header outlives a navigation between articles, so the query would survive it.
  useEffect(() => {
    setQuery('')
  }, [pathname])

  useEffect(() => {
    const handler = setTimeout(() => {
      if (trimmedQuery) {
        analytics.track('Academy search', {debouncedQuery: trimmedQuery})
      }
    }, analyticsDebounceIntervalMs)

    return () => clearTimeout(handler)
  }, [trimmedQuery])

  const words = normalize(trimmedQuery).split(/\s+/).filter(Boolean)
  const articleLinks = contents
    .flatMap((category) => category.articles)
    .filter(({title}) => words.every((word) => normalize(title).includes(word)))
    .map(({title, slug}) => ({title, linkHref: routeTo.tmAcademy.article(slug)}))

  return {query, setQuery, hasQuery: trimmedQuery !== '', articleLinks}
}
